<template>
  <div style="background-color: #fff">
    <center>
      <h2>TIREVIEW - F.A.S.T</h2>
      <h2 class="no-sep" v-if="order.replacementOrder">REPLACEMENT ORDER #{{order.number}}</h2>
      <h2 class="no-sep" v-else>ORDER #{{order.number}}</h2>
    </center>

    <div>
      <table>
        <tbody>
          <tr>
            <th>Order number</th>
            <th>Items</th>
            <td :rowspan="isPlatform2 ? 12 : 10">
              <img :src="qrcodeImage">
            </td>
          </tr>
          <tr>
            <td>#{{order.number}}</td>
            <td>{{(order.items || []).length}}</td>
          </tr>

          <template v-if="isPlatform2">
            <tr>
              <th>Organization</th>
              <th>Devices</th>
            </tr>
            <tr>
              <td>{{organizationDisplay}}</td>
              <td>{{totalDevices}}</td>
            </tr>
            <tr>
              <th>Account</th>
              <th>Sensors</th>
            </tr>
            <tr>
              <td>{{accountDisplay}}</td>
              <td>{{totalSensors}}</td>
            </tr>
          </template>

          <template v-else>
            <tr>
              <th>Client</th>
              <th>Devices</th>
            </tr>
            <tr>
              <td>#{{order.account.value}} - {{order.account.label}}</td>
              <td>{{totalDevices}}</td>
            </tr>
            <tr>
              <th width="70%">System</th>
              <th width="15%">Sensors</th>
            </tr>
            <tr>
              <td>{{order.account.systemName}}</td>
              <td>{{totalSensors}}</td>
            </tr>
          </template>

          <tr>
            <th>Term</th>
            <th>Period</th>
          </tr>
          <tr>
            <td>{{termDisplay}}</td>
            <td>{{periodDisplay}}</td>
          </tr>
          <tr>
            <th>Part Number</th>
            <th>Rate</th>
          </tr>
          <tr>
            <td>{{partNumberDisplay}}</td>
            <td>{{rateDisplay}}</td>
          </tr>
          <template v-if="isPlatform2">
            <tr>
              <th colspan="2">Gateway</th>
            </tr>
            <tr>
              <td colspan="2">{{gatewayDisplay}}</td>
            </tr>
          </template>
        </tbody>
      </table>

      <h3>ITEMS</h3>
      <table>
        <thead>
          <tr>
            <th width="50">#</th>
            <th width="80">ID</th>
            <th>Vehicle Type</th>
            <th v-if="!isPlatform2">Locations</th>
            <th width="15%">Quantity</th>
            <th width="15%">Sensors</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) of (order.items || [])" :key="item.id">
            <th>{{index + 1}}</th>
            <td>{{vehicleTypeId(item)}}</td>
            <td>{{vehicleTypeName(item)}}</td>
            <td v-if="!isPlatform2">{{(item.locations || []).length > 0 ? item.locations.map(i => i.name).join(', ') : '(None)'}}</td>
            <td>{{item.quantity}}</td>
            <td>{{item.vehicle && item.vehicle.sensors}}</td>
          </tr>
        </tbody>
      </table>

      <div class="issued">Issued at {{currentTime}} by {{order.issuedBy}}</div>
    </div>
  </div>
</template>

<script>
import qrcode from 'qrcode'

export default {
  name: 'OrderGeneratorPrintView',

  methods: {
    getOrder () {
      const raw = sessionStorage.getItem('order-generator')
      const parsed = raw ? JSON.parse(raw) : {}
      this.order = {
        account: {},
        items: [],
        ...parsed
      }
      if (!this.order.account || typeof this.order.account !== 'object') {
        this.order.account = {}
      }
      if (!Array.isArray(this.order.items)) {
        this.order.items = []
      }
    },

    vehicleTypeId (item) {
      if (item.deviceType && item.deviceType.id != null) return item.deviceType.id
      if (item.batchSettings && item.batchSettings.id != null) return item.batchSettings.id
      return '—'
    },

    vehicleTypeName (item) {
      if (item.deviceType && item.deviceType.name) return item.deviceType.name
      if (item.batchSettings && item.batchSettings.name) return item.batchSettings.name
      return '—'
    }
  },

  computed: {
    isPlatform2 () {
      return this.order.platform === 'platform2'
    },

    organizationDisplay () {
      const org = this.order.organization || {}
      const id = org.value || org.id
      const name = org.label || org.name
      if (id && name) return `#${id} - ${name}`
      return name || id || '—'
    },

    accountDisplay () {
      const account = this.order.account || {}
      if (account.value && account.label) return `#${account.value} - ${account.label}`
      return account.label || account.value || '—'
    },

    gatewayDisplay () {
      const gateway = this.order.gateway || {}
      if (typeof gateway === 'string') return gateway || '—'
      return gateway.label || gateway.value || '—'
    },

    termDisplay () {
      const term = this.order.termType
      if (term == null || typeof term === 'string') return term || '—'
      return term.label || term.value || '—'
    },

    periodDisplay () {
      const period = this.order.period
      if (period == null || typeof period === 'string') {
        if (period === 'custom' && this.order.periodTime) return `Custom (${this.order.periodTime})`
        return period || '—'
      }
      if (period.value === 'custom') {
        return this.order.periodTime
          ? `Custom (${this.order.periodTime})`
          : (period.label || 'Custom')
      }
      return period.label || period.value || '—'
    },

    partNumberDisplay () {
      const partNumber = this.order.partNumber
      if (partNumber == null || typeof partNumber === 'string') return partNumber || '—'
      return partNumber.label || partNumber.value || '—'
    },

    rateDisplay () {
      const rate = this.order.rate
      if (rate == null || rate === '') return '—'
      return String(rate)
    },

    totalDevices () {
      return (this.order.items || []).reduce((a, b) => a + b.quantity, 0)
    },

    totalSensors () {
      return (this.order.items || []).reduce((a, b) => a + ((b.vehicle && b.vehicle.sensors) || 0), 0)
    },

    currentTime () {
      return new Date().toLocaleString()
    },

    qrcodeText () {
      if (this.isPlatform2) {
        return this.platform2QrcodeText
      }
      return this.platform1QrcodeText
    },

    platform1QrcodeText () {
      const account = this.order.account || {}
      const items = (this.order.items || []).map(i => [i.batchSettings.id, i.quantity, (i.locations || []).map(j => j.id)].join(',')).join('#')
      const parts = [
        this.order.number,
        account.systemId,
        account.value,
        this.termPayload,
        items
      ]
      return '>>>p1|' + parts.join('|')
    },

    platform2QrcodeText () {
      const organizationId = this.order.organization && this.order.organization.value
      const accountId = this.order.account && this.order.account.value
      const gateway = this.order.gateway && (this.order.gateway.value || this.order.gateway)
      const items = (this.order.items || []).map(i => {
        const id = (i.deviceType && i.deviceType.id) || (i.batchSettings && i.batchSettings.id)
        return [id, i.quantity].join(',')
      }).join('#')
      const parts = [
        this.order.number,
        organizationId,
        accountId,
        gateway,
        this.termPayload,
        items
      ]
      return '>>>p2|' + parts.join('|')
    },

    termPayload () {
      const termVal = this.order.termType && this.order.termType.value
      let termWithPeriod = termVal != null ? String(termVal) : ''

      if (termVal === 'prepaid') {
        const periodVal = this.order.period && this.order.period.value
        if (periodVal === 'custom' && String(this.order.periodTime || '').trim()) {
          termWithPeriod = 'prepaid,' + encodeURIComponent(String(this.order.periodTime).trim())
        } else if (periodVal && periodVal !== 'custom') {
          termWithPeriod = 'prepaid,' + String(periodVal)
        }
      }

      const partNumberVal = this.order.partNumber && this.order.partNumber.value

      if (partNumberVal) {
        termWithPeriod = termWithPeriod + ',' + encodeURIComponent(String(partNumberVal).trim())
      }

      if (this.order.rate != null && String(this.order.rate).trim() !== '') {
        termWithPeriod = termWithPeriod + ',' + encodeURIComponent(String(this.order.rate).trim())
      }

      return termWithPeriod
    }
  },

  watch: {
    qrcodeText: {
      immediate: true,
      async handler () {
        this.qrcodeImage = await qrcode.toDataURL(this.qrcodeText, {
          errorCorrectionLevel: 'M'
        })
      }
    }
  },

  data () {
    return {
      order: {
        account: {},
        items: []
      },
      qrcodeImage: ''
    }
  },

  created () {
    this.getOrder()
  },

  mounted () {
    setTimeout(() => {
      window.print()
    }, 5000)
  }
}
</script>

<style lang="less" scoped>
h2 {
  padding: 10px 0;
  border-bottom: 1px solid #000;

  &.no-sep {
    border: none;
  }
}

h3 {
  margin-top: 20px;
}

table {
  width: 100%;
  border: 1px solid #000;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #000;
    padding: 5px;
    text-align: center;
  }

  th {
    background-color: #eee;
    print-color-adjust: exact !important;

    &.darken-bg {
      background-color: #ccc;
    }
  }

  tbody {
    border: none;
  }
}

.issued {
  padding-top: 30px;
  opacity: 0.7;
  font-style: italic;
  font-size: 10pt;
}
</style>
